import { useState, useMemo, use, useEffect, useCallback, useRef } from 'react';
import { useUrl } from 'crossroad';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X } from 'lucide-react';
import { BIBLE_BOOKS, BIBLE_BOOKS_MAP, getBookSectionColor, type BibleBook, type BibleBookInfo } from '@/lib/utils/bible-utils';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { cn } from '@/lib/utils/utils';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { saveScrollPosition } from '@/lib/utils/scroll-position';
import { parseRef } from '@/lib/utils/reference-utils';
import { Analytics } from '@/lib/analytics';
import { debounce } from '@/lib/utils/utils';

// Static constants - moved outside component to avoid recreation
export const SECTION_LABELS: Record<string, string> = {
    pentateuch: 'Torah',
    historical: 'Historical Books',
    wisdom: 'Wisdom Literature',
    majorProphets: 'Major Prophets',
    minorProphets: 'Minor Prophets',
    gospelsAndActs: 'Gospels & Acts',
    paulineEpistles: 'Pauline Epistles',
    generalEpistles: 'General Epistles',
    revelation: 'Revelation'
};

const SECTION_ORDER: string[] = [
    'pentateuch',
    'historical',
    'wisdom',
    'majorProphets',
    'minorProphets',
    'gospelsAndActs',
    'paulineEpistles',
    'generalEpistles',
    'revelation'
];

interface PassageSelectorContentProps {
    currentBook: BibleBook;
    currentChapter: number;
    currentPassageId?: string | null;
    isDesktop: boolean;
    onClose: () => void;
}

export function PassageSelectorContent({ currentBook, currentChapter, currentPassageId, isDesktop, onClose }: PassageSelectorContentProps) {
    const [, setUrl] = useUrl();
    const { setScrollState } = usePassageState();
    const { data: passageData } = use(PassageDataContext);
    const [search, setSearch] = useState('');
    const [selectedBook, setSelectedBook] = useState<BibleBook>(currentBook);

    // Refs for scrolling to selected items
    const booksScrollRef = useRef<HTMLDivElement>(null);
    const chaptersScrollRef = useRef<HTMLDivElement>(null);
    const passagesScrollRef = useRef<HTMLDivElement>(null);

    // Get passages for the selected book
    const passagesForBook = useMemo(() => {
        if (!passageData?.items) {
            return [];
        }
        return passageData.items.filter(passage => passage.book === selectedBook);
    }, [passageData?.items, selectedBook]);

    // Group books by section
    const groupedBooks = useMemo(() => {
        const books = search
            ? BIBLE_BOOKS.filter(book => book.name.toLowerCase().includes(search.toLowerCase()))
            : BIBLE_BOOKS;

        const groups: Record<string, BibleBookInfo[]> = {};
        books.forEach(book => {
            if (!groups[book.section]) {
                groups[book.section] = [];
            }
            groups[book.section].push(book);
        });
        return groups;
    }, [search]);

    // Generate chapter numbers for selected book
    const chapters = useMemo(() => {
        const bookInfo = BIBLE_BOOKS_MAP.get(selectedBook);
        const count = bookInfo?.numChapters || 0;
        return Array.from({ length: count }, (_, i) => i + 1);
    }, [selectedBook]);

    const handleSelectChapter = useCallback((chapter: number) => {
        setScrollState({
            book: selectedBook,
            chapter,
            verse: null
        });

        // Update read mode cache
        saveScrollPosition(selectedBook, chapter, 1);

        // Navigate to the book/chapter WITHOUT query parameters
        setUrl(`/app/read/${selectedBook}/${chapter}`);
        onClose();
    }, [selectedBook, setUrl, onClose, setScrollState]);

    const handleSelectPassage = useCallback((passageId: string) => {
        // Find the passage to extract its reference for the read mode cache
        const selectedPassage = passageData?.items.find(p => p.id === passageId);
        if (selectedPassage) {
            const range = parseRef(selectedPassage.ref);
            if (range) {
                saveScrollPosition(selectedPassage.book as BibleBook, range.chapterStart, range.verseStart);
            }
        }

        // Navigate to passage details WITHOUT query parameters
        setUrl(`/app/passage/${passageId}`);
        onClose();
    }, [passageData, setUrl, onClose]);

    const handleSelectBook = useCallback((book: BibleBook) => {
        // If no passage is selected, jump directly to chapter 1
        if (!currentPassageId) {
            setScrollState({
                book,
                chapter: 1,
                verse: null
            });
            saveScrollPosition(book, 1, 1);
            setUrl(`/app/read/${book}/1`);
            onClose();
        } else {
            // Otherwise, just select the book to show its chapters
            setSelectedBook(book);
        }
    }, [currentPassageId, setScrollState, setUrl, onClose]);

    const trackSearch = useMemo(() =>
        debounce((query: string, count: number) => {
            if (query.length >= 2) {
                Analytics.search(query, count);
            }
        }, 1000), []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);

        // Count total results across all sections
        const resultsCount = BIBLE_BOOKS.filter(book =>
            book.name.toLowerCase().includes(value.toLowerCase())
        ).length;

        trackSearch(value, resultsCount);
    }, [trackSearch]);

    // Scroll to selected items when component mounts
    useEffect(() => {
        // Small delay to ensure DOM is rendered
        setTimeout(() => {
            // Scroll to current book
            const currentBookButton = booksScrollRef.current?.querySelector(`[data-book="${currentBook}"]`);
            currentBookButton?.scrollIntoView({ block: 'center', behavior: 'instant' });

            // Scroll to current chapter
            const currentChapterButton = chaptersScrollRef.current?.querySelector(`[data-chapter="${currentChapter}"]`);
            currentChapterButton?.scrollIntoView({ block: 'center', behavior: 'instant' });

            // Scroll to current passage
            if (currentPassageId) {
                const currentPassageButton = passagesScrollRef.current?.querySelector(`[data-passage="${currentPassageId}"]`);
                currentPassageButton?.scrollIntoView({ block: 'center', behavior: 'instant' });
            }
        }, 100);
    }, [currentBook, currentChapter, currentPassageId]);

    // Handle Escape key to clear search
    const handleEscapeKey = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape' && search) {
            e.preventDefault();
            setSearch('');
        }
    }, [search]);

    useEffect(() => {
        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [handleEscapeKey]);

    return (
        <>
            {/* Search Input */}
            <div className="p-4 border-b">
                <div className="relative flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="search"
                            placeholder="Search books (e.g. Colossians)"
                            value={search}
                            onChange={handleSearchChange}
                            className="pl-9"
                            autoFocus={isDesktop}
                        />
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className="flex-shrink-0"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Column Headers */}
            <div className={cn(
                "grid border-b bg-muted/50",
                currentPassageId
                    ? (isDesktop ? "grid-cols-[160px_1fr]" : "grid-cols-[160px_1fr]")
                    : (isDesktop ? "grid-cols-[160px_1fr]" : "grid-cols-[160px_1fr]")
            )}>
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-r">
                    BOOK
                </div>
                {currentPassageId ? (
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                        PASSAGE
                    </div>
                ) : (
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                        CHAPTER
                    </div>
                )}
            </div>

            {/* 2-Column Layout */}
            <div className={cn(
                "grid grid-cols-[160px_1fr]",
                isDesktop ? "h-[500px]" : "h-[calc(100vh-48px-69px-32px)]"
            )}>
                {/* Books Column */}
                <ScrollArea className="border-r h-full -mt-2">
                    <div className="py-2 h-full overflow-y-auto" ref={booksScrollRef}>
                        {Object.keys(groupedBooks).length === 0 && (
                            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                                No books found
                            </div>
                        )}
                        {SECTION_ORDER.map((section) => {
                            const books = groupedBooks[section];
                            if (!books || books.length === 0) return null;

                            return (
                                <div key={section} className="mb-3 last:mb-0">
                                    {/* Section Header */}
                                    <div className="px-3 pt-2 pb-1 text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider">
                                        {SECTION_LABELS[section]}
                                    </div>

                                    {/* Books in Section */}
                                    {books.map((book) => {
                                        const isSelected = selectedBook === book.name;
                                        const sectionColor = getBookSectionColor(book.name);
                                        return (
                                            <Button
                                                key={book.name}
                                                variant="ghost"
                                                onClick={() => handleSelectBook(book.name)}
                                                data-book={book.name}
                                                className={cn(
                                                    "w-full justify-start px-3 py-1.5 h-auto font-normal border-l-4 whitespace-nowrap",
                                                    sectionColor,
                                                    isSelected && "bg-primary text-primary-foreground font-medium hover:bg-primary/90 hover:text-primary-foreground"
                                                )}
                                            >
                                                {book.name}
                                            </Button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>

                {/* Chapters Column - Only show when currentPassageId is NOT set */}
                {!currentPassageId && (
                    <ScrollArea className="bg-muted/30 h-full">
                        <div className="p-2 gap-1 h-full overflow-y-auto grid grid-cols-4" ref={chaptersScrollRef}>
                            {chapters.map((chapter) => {
                                const isCurrent = currentBook === selectedBook && currentChapter === chapter;
                                return (
                                    <Button
                                        key={chapter}
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSelectChapter(chapter)}
                                        data-chapter={chapter}
                                        className={cn(
                                            "h-auto px-2 py-2 font-normal",
                                            isCurrent && "bg-primary text-primary-foreground font-medium hover:bg-primary/90"
                                        )}
                                    >
                                        {chapter}
                                    </Button>
                                );
                            })}
                        </div>
                    </ScrollArea>
                )}

                {/* Passages Column - Only show when currentPassageId IS set */}
                {currentPassageId && (
                    <ScrollArea className="h-full">
                        <div className="p-2 h-full overflow-y-auto" ref={passagesScrollRef}>
                            {passagesForBook.length > 0 ? (
                                passagesForBook.map((passage) => {
                                    const isCurrent = currentPassageId === passage.id;
                                    return (
                                        <Button
                                            key={passage.id}
                                            variant={isCurrent ? "default" : "ghost"}
                                            onClick={() => handleSelectPassage(passage.id)}
                                            data-passage={passage.id}
                                            className={cn(
                                                "w-full justify-start px-3 py-1.5 h-auto font-normal flex-col items-start gap-0 whitespace-normal text-left group",
                                                !isCurrent && "hover:bg-accent"
                                            )}
                                        >
                                            <span className="text-sm font-medium">{passage.ref}</span>
                                            <span className={cn(
                                                "text-xs",
                                                isCurrent ? "text-primary-foreground/80" : "text-muted-foreground group-hover:text-foreground/80"
                                            )}>
                                                {passage.title}
                                            </span>
                                        </Button>
                                    );
                                })
                            ) : (
                                <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No passages available
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                )}
            </div>
        </>
    );
}

